import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import CategoryIcon from '@mui/icons-material/Category';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CallToActionIcon from '@mui/icons-material/CallToAction';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import { useTranslation } from 'react-i18next';

interface FeaturesMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  bulkActionMode: boolean;
  loading: boolean;
  loadingExport: boolean;
  n11IntegrationActive: boolean;
  isMobile: boolean;
  onOpenCategoryModal: () => void;
  onOpenProductFeatureModal: () => void;
  onOpenBrandModal: () => void;
  onOpenWarehouseModal: () => void;
  onStockMovementsReport: () => void;
  onToggleBulkActionMode: () => void;
  onNavigateToImageLibrary: () => void;
  onOpenTableSettings: () => void;
  onOpenShipmentTemplates: () => void;
  onExportToExcel: () => void;
  onOpenImportModal: () => void;
  onOpenN11Upload: () => void;
  onOpenN11Log: () => void;
  onOpenOtherActionsModal: () => void;
  onPriceChangeLogs: () => void;
  onOpenInventoryInfo: () => void;
}

export const FeaturesMenu: React.FC<FeaturesMenuProps> = ({
  anchorEl,
  open,
  onClose,
  bulkActionMode,
  loading,
  loadingExport,
  n11IntegrationActive,
  isMobile,
  onOpenCategoryModal,
  onOpenProductFeatureModal,
  onOpenBrandModal,
  onOpenWarehouseModal,
  onStockMovementsReport,
  onToggleBulkActionMode,
  onNavigateToImageLibrary,
  onOpenTableSettings,
  onOpenShipmentTemplates,
  onExportToExcel,
  onOpenImportModal,
  onOpenN11Upload,
  onOpenN11Log,
  onOpenOtherActionsModal,
  onPriceChangeLogs,
  onOpenInventoryInfo,
}) => {
  const { t } = useTranslation('accounting');
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem onClick={() => {
        onClose();
        onOpenInventoryInfo();
      }}>
        <ListItemIcon>
          <InfoOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.info')} />
      </MenuItem>

      <MenuItem onClick={() => {
        onClose();
        onStockMovementsReport();
      }}>
        <ListItemIcon>
          <AssessmentIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.reports')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenCategoryModal();
      }}>
        <ListItemIcon>
          <CategoryIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.categories')} />
      </MenuItem>

      <MenuItem onClick={() => {
        onClose();
        onOpenProductFeatureModal();
      }}>
        <ListItemIcon>
          <TuneIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.productFeatures')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenBrandModal();
      }}>
        <ListItemIcon>
          <BrandingWatermarkIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.brands')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenWarehouseModal();
      }}>
        <ListItemIcon>
          <WarehouseIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.warehouses')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onToggleBulkActionMode();
      }}>
        <ListItemIcon>
          <CallToActionIcon fontSize="small" color={bulkActionMode ? "warning" : "primary"} />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.bulkActions')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onNavigateToImageLibrary();
      }}>
        <ListItemIcon>
          <PhotoLibraryIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.imageLibrary')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenTableSettings();
      }}>
        <ListItemIcon>
          <ViewColumnIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.customizeTable')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenShipmentTemplates();
      }}>
        <ListItemIcon>
          <LocalShippingIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.shipmentTemplates')} />
      </MenuItem>
      
      <MenuItem 
        onClick={() => {
          onClose();
          onExportToExcel();
        }}
        disabled={loadingExport || loading}
      >
        <ListItemIcon>
          <FileDownloadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={loadingExport ? t('stock.common.preparing') : t('stock.menu.downloadExcel')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenImportModal();
      }}>
        <ListItemIcon>
          <FileUploadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.importStock')} />
      </MenuItem>
      
      <MenuItem 
        disabled={true}
        sx={{
          opacity: 0.6,
          '&.Mui-disabled': {
            color: 'text.secondary'
          }
        }}
      >
        <ListItemIcon>
          <CloudUploadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
            primary={t('stock.menu.hepsiburadaCatalog')} 
            secondary={t('stock.menu.comingSoon')}
        />
      </MenuItem>
      
      <MenuItem 
        disabled={true}
        sx={{
          opacity: 0.6,
          '&.Mui-disabled': {
            color: 'text.secondary'
          }
        }}
      >
        <ListItemIcon>
          <CloudUploadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
            primary={t('stock.menu.trendyolCatalog')} 
            secondary={t('stock.menu.comingSoon')}
        />
      </MenuItem>
      
      <MenuItem 
        onClick={() => {
          onClose();
          onOpenN11Upload();
        }}
        disabled={!n11IntegrationActive}
        sx={{
          opacity: n11IntegrationActive ? 1 : 0.6,
          '&.Mui-disabled': {
            color: 'text.secondary'
          }
        }}
      >
        <ListItemIcon>
          <CloudUploadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary={`${t('stock.menu.n11Catalog')} ${n11IntegrationActive ? '✅' : '❌'}`}
          secondary={!n11IntegrationActive ? t('stock.menu.enterIntegrationInfo') : undefined}
        />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onOpenN11Log();
      }}>
        <ListItemIcon>
          <AssessmentIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.marketplaceLogs')} />
      </MenuItem>
      
      <MenuItem onClick={() => {
        onClose();
        onPriceChangeLogs();
      }}>
        <ListItemIcon>
          <HistoryIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={t('stock.menu.priceChangeLogs')} />
      </MenuItem>
      
      {isMobile && (
        <MenuItem onClick={() => {
          onClose();
          onOpenOtherActionsModal();
        }}>
          <ListItemIcon>
            <MoreHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('stock.menu.otherActions')} />
        </MenuItem>
      )}
    </Menu>
  );
};

